import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, switchMap, tap } from 'rxjs';

import { Device, IHDspace, SpacesDispensesHD } from '../../interfaces/IHDspace';
import { Order } from '../../interfaces/IOrder';
import { IActions, IScheduleActionParams } from '../../interfaces/IScheduleActionParams';
import { ISettings } from '../../interfaces/ISettings';
import { ISubmitBody } from '../../interfaces/ISubmitBody';
import { retryStrategy } from '../../utils';
import { resolveDeviceId } from '../../utils/device-id.util';
import { EndpointService } from '../endpoint/endpoint.service';

import {
  APP_ID,
  DISPENSE_ACTIONS,
  OVERFLOW_AREA_SPACE_ID,
  SPACE_STATES,
} from './../../utils/constants';
import { HelmsdeepServiceAPI } from './helmsdeep.service.api';
import { HelmsdeepValidateCodeErrorResponse } from './helmsdeep-validate-code-error.response';
import { ValidateCodeResponse } from './types';

export const HELMDEEP_ENDPOINTS = {
  SPACES: 'spaces',
  DEVICES: 'devices',
  SETTINGS: 'settings',
  ACCESS_CODES: 'accessCodes',
} as const;

@Injectable({
  providedIn: 'root',
})
export class HelmsdeepService extends HelmsdeepServiceAPI {
  gandalfUrl: string;
  helmsdeepUrl: string;
  headers = { 'content-type': 'application/json' };
  settingsPath = '/settings';
  private deviceIdOverride: string | null = null;

  /**
   * Resolves per-request: an explicit override (set via the setter, mainly
   * for tests) takes precedence, then the current `EndpointService.nodeId`
   * (URL query param / sessionStorage, resolved fresh on every read so it
   * reflects the active session rather than whatever it was at construction
   * time), then finally the static `DEVICE_ID` deploy-time constant as a
   * last resort for genuine single-tenant device-mode deployments.
   */
  get deviceId(): string {
    return this.deviceIdOverride ?? resolveDeviceId(null, this.endpointService.nodeId);
  }

  set deviceId(value: string) {
    this.deviceIdOverride = value;
  }

  constructor(http: HttpClient, endpointService: EndpointService) {
    super(http, endpointService);
    this.gandalfUrl = `${this.endpointService.getGandalfUrl()}/api/submit`;
    this.helmsdeepUrl = this.endpointService.getHelmsdeepUrl();
  }

  addComparment(dispenseId: string, side?: string): Observable<object> {
    {
      const addCompartment = {
        appId: APP_ID,
        method: 'POST',
        url: `dispenses/${dispenseId}/_actions`,
        payload: [
          {
            type: 'ADD_COMPARTMENT',
            params: {
              device: this.deviceId,
              meta: {
                ...(side && { side }),
              },
            },
          },
        ],
      };

      return this.http
        .post(this.gandalfUrl, addCompartment, { headers: this.headers })
        .pipe(tap((compartment) => console.log('add compartment: ', compartment)));
    }
  }

  resetSpace(spaceId: string): Observable<object> {
    const body: ISubmitBody = {
      appId: APP_ID,
      method: 'PUT',
      url: `spaces/${spaceId}/_reset`,
    };
    return this.http.post(this.gandalfUrl, body, { headers: this.headers });
  }

  actions(dataToLoad: IActions[]): Observable<object> {
    {
      const payload = dataToLoad.map((action) => ({
        ...action,
        params: action.params
          ? {
              ...action.params,
              device: this.deviceId,
            }
          : {
              device: this.deviceId,
            },
      }));

      const doAction = {
        appId: APP_ID,
        method: 'POST',
        url: 'actions',
        payload,
      };

      return this.http
        .post(this.gandalfUrl, doAction, { headers: this.headers })
        .pipe(tap((compartment) => console.log('actions: ', compartment)));
    }
  }

  getMyDevice(params: { includeSpaces?: boolean } = {}): Observable<Device> {
    return this.getDevices({
      id: this.deviceId,
      ...params,
    }) as Observable<Device>;
  }

  getDevices(params: { id: string; includeSpaces?: boolean }): Observable<Device>;
  getDevices(params?: { includeSpaces?: boolean }): Observable<Array<Device>>;
  getDevices(
    params: { id?: string; includeSpaces?: boolean } = {}
  ): Observable<Device | Array<Device>> {
    const { includeSpaces, id } = params;
    const url = new URL(`${this.helmsdeepUrl}/${HELMDEEP_ENDPOINTS.DEVICES}${id ? `/${id}` : ''}`);
    if (includeSpaces) {
      url.searchParams.set('include_docs', 'spaces');
    }
    return this.http.get<Device | Array<Device>>(url.toString(), {
      headers: this.headers,
    });
  }

  getSpaces(
    params: {
      includeDispenses?: boolean;
      position?: string;
      presence?: typeof SPACE_STATES.FILL | typeof SPACE_STATES.UNFILL;
      secure?: typeof SPACE_STATES.CLOSE | typeof SPACE_STATES.OPEN;
      selectable?: typeof SPACE_STATES.AVAILABLE | typeof SPACE_STATES.RESERVE;
      count?: boolean;
    } = {}
  ): Observable<Array<IHDspace | SpacesDispensesHD>> {
    const { includeDispenses, position, presence, secure, selectable, count } = params;
    const url = new URL(`${this.helmsdeepUrl}/${HELMDEEP_ENDPOINTS.SPACES}`);
    url.searchParams.set('device', this.deviceId);

    if (includeDispenses) {
      url.searchParams.set('include_docs', 'dispenses');
    }
    if (position) {
      url.searchParams.set('position', position);
    }
    if (presence) {
      url.searchParams.set('presence', presence);
    }
    if (secure) {
      url.searchParams.set('secure', secure);
    }
    if (selectable) {
      url.searchParams.set('selectable', selectable);
    }
    if (count) {
      url.searchParams.set('count', 'true');
    }

    return this.http
      .get<Array<IHDspace | SpacesDispensesHD>>(url.toString(), {
        headers: this.headers,
      })
      .pipe(retryStrategy());
  }

  getAvailableSpacesCount(): Observable<{ count: number }> {
    return this.getSpaces({
      presence: 'unfilled',
      secure: 'close',
      selectable: 'available',
      count: true,
    }) as unknown as Observable<{ count: number }>;
  }

  getScheduleByCode(code: string): Observable<object> {
    const finishLoadOrder: ISubmitBody = {
      appId: APP_ID,
      method: 'GET',
      url: `schedules?code=${code}&include_docs=dispenses`,
    };

    return this.http
      .post(this.gandalfUrl, finishLoadOrder, { headers: this.headers })
      .pipe(tap((schedule: any) => console.log('Get Schedule Action By Code:', schedule)));
  }

  getDispenseById(id: string): Observable<object> {
    const url = `${this.helmsdeepUrl}/dispenses/${id}?include_docs=space`;

    return this.http.get(url, { headers: this.headers });
  }

  validateCode(
    code: string,
    allowedCodeTypes: readonly string[]
  ): Observable<ValidateCodeResponse> {
    const url = `${this.helmsdeepUrl}/${HELMDEEP_ENDPOINTS.ACCESS_CODES}/_actions`;
    const payload = [
      {
        type: 'CODE_VALIDATE',
        dryRun: true,
        params: {
          device: this.deviceId,
          code,
          allowedCodeTypes,
        },
      },
    ];

    return this.http.post<ValidateCodeResponse>(url, payload, { headers: this.headers }).pipe(
      map((response) => {
        if (response.error) {
          throw new HelmsdeepValidateCodeErrorResponse(response.error);
        } else if (response instanceof Array) {
          return response[0] as ValidateCodeResponse;
        }
        return response;
      })
    );
  }

  scheduleAction(scheduleActionParams: IScheduleActionParams): Observable<object> {
    const scheduleAction: ISubmitBody = {
      appId: APP_ID,
      method: 'POST',
      url: `schedules/${scheduleActionParams.scheduleId}/_actions`,
      payload: [
        {
          type: scheduleActionParams.type,
          params: {
            device: this.deviceId,
            dispenses: scheduleActionParams.dispenses,
            spaceId: scheduleActionParams.spaceId,
            actionType: scheduleActionParams.actionType,
            smartDoor: scheduleActionParams.smartDoor,
            ...(scheduleActionParams.size && {
              size: scheduleActionParams.size,
            }),
            meta: {
              ...(scheduleActionParams?.side && { side: scheduleActionParams.side }),
              hmi: scheduleActionParams.hmi,
              app: scheduleActionParams.app,
            },
          },
        },
      ],
    };

    return this.http
      .post(this.gandalfUrl, scheduleAction, { headers: this.headers })
      .pipe(tap((schedule) => console.log('scheduleAction: ', schedule)));
  }

  finishLoadOrder(scheduleId: string, side?: string): Observable<object> {
    const finishLoadOrder: ISubmitBody = {
      appId: APP_ID,
      method: 'POST',
      url: `schedules/${scheduleId}/_actions`,
      payload: [
        {
          type: 'DO_TRANSACTION',
          params: {
            actionType: 'FINISH_LOAD',
            meta: {
              ...(side && { side }),
            },
          },
        },
      ],
    };

    return this.http
      .post(this.gandalfUrl, finishLoadOrder, { headers: this.headers })
      .pipe(tap((schedule) => console.log('finish load order: ', schedule)));
  }

  finishDispense(scheduleId: string, side?: string): Observable<object> {
    const finishDispenseOrder: ISubmitBody = {
      appId: APP_ID,
      method: 'POST',
      url: `schedules/${scheduleId}/_actions`,
      payload: [
        {
          type: 'DO_TRANSACTION',
          params: {
            actionType: 'FINISH_DISPENSE',
            meta: {
              ...(side && { side }),
            },
          },
        },
      ],
    };

    return this.http
      .post(this.gandalfUrl, finishDispenseOrder, { headers: this.headers })
      .pipe(tap((schedule) => console.log('finish dispense order: ', schedule)));
  }

  unfillSpaces(spaces: Array<string>): Observable<object> {
    const spaceResourceInfo = [];
    spaces.forEach((space) => {
      spaceResourceInfo.push({
        resourceId: space,
        type: 'UNFILL',
        params: {
          spaceId: space,
        },
      });
    });
    const unfillAction: ISubmitBody = {
      appId: APP_ID,
      method: 'POST',
      url: 'actions',
      payload: spaceResourceInfo,
    };

    return this.http
      .post(this.gandalfUrl, unfillAction, { headers: this.headers })
      .pipe(tap((spaces: any) => console.log('spaces are available now:', spaces)));
  }

  createDispenses(dispenses: any[]): Observable<any> {
    const body = {
      appId: APP_ID,
      method: 'POST',
      url: 'dispenses',
      payload: dispenses,
    };

    return this.http
      .post(this.gandalfUrl, body, { headers: this.headers })
      .pipe(
        tap((dispenseItem: any) => console.log('create dispenses:', JSON.stringify(dispenseItem)))
      );
  }

  updateReqtoActivate() {
    const settingsUrl = `${this.helmsdeepUrl}${this.settingsPath}`;
    this.http
      .get(settingsUrl)
      .pipe(
        switchMap((hdSettings: ISettings) => {
          return this.updateIsActivated(hdSettings, settingsUrl);
        })
      )
      .subscribe(
        (item) => {
          console.log(item);
        },
        (error) => console.error(error)
      );
  }

  updateIsActivated(hdSettings: ISettings, settingsUrl: string): Observable<any> {
    const newvar = {
      ...hdSettings,
      device: {
        ...hdSettings.device,
        isActivated: true,
      },
    };
    return this.http
      .post(settingsUrl, newvar)
      .pipe(tap((resp) => console.log('Device activation updated', resp)));
  }

  getCurrentDispense(scheduleId: string, state: string | string[]): Observable<boolean> {
    return this.getScheduleById(scheduleId, { includeDispenses: true }).pipe(
      map((schedule: Order) => {
        const currentDispense = schedule.dispenses.find((dispense) => {
          if (Array.isArray(state)) {
            return state.includes(dispense.state);
          }
          return dispense.state === state;
        });
        return currentDispense?.verify ? currentDispense.verify : false;
      })
    );
  }

  loadOrder(scheduleId: string, side?: string, enableOverflowArea?: boolean): Observable<object> {
    const scheduleToload: ISubmitBody = {
      appId: APP_ID,
      method: 'POST',
      url: `schedules/${scheduleId}/_actions`,
      payload: [
        {
          type: DISPENSE_ACTIONS.DO_TRANSACTION,
          params: {
            device: this.deviceId,
            actionType: DISPENSE_ACTIONS.DO_LOAD,
            meta: {
              ...(side && { side }),
            },
            ...(enableOverflowArea ? { spaceId: OVERFLOW_AREA_SPACE_ID } : {}),
          },
        },
      ],
    };
    return this.http
      .post(this.gandalfUrl, scheduleToload, { headers: this.headers })
      .pipe(tap((order) => console.log('load order: ', order)));
  }

  orderHandOff(scheduleId: string, handedOffReason: string[] = []) {
    const reasons = (handedOffReason || []).length > 0 ? { handedOffReason } : {};

    const scheduleHandOff = {
      appId: APP_ID,
      method: 'POST',
      url: `schedules/${scheduleId}/_actions`,
      payload: [
        {
          type: DISPENSE_ACTIONS.DO_TRANSACTION,
          params: {
            actionType: DISPENSE_ACTIONS.DO_HANDOFF,
            ...reasons,
          },
        },
      ],
    };

    return this.http
      .post(this.gandalfUrl, scheduleHandOff, { headers: this.headers })
      .pipe(tap((order) => console.log('handed off order: ', order)));
  }

  getScheduleById(
    scheduleId: string,
    params: { includeDispenses?: boolean } = {}
  ): Observable<Order> {
    const { includeDispenses } = params;
    const url = new URL(`${this.helmsdeepUrl}/schedules/${scheduleId}`);
    if (includeDispenses) {
      url.searchParams.set('include_docs', 'dispenses');
    }
    return this.http.get<Order>(url.toString(), { headers: this.headers });
  }
}
